import { useState } from "react"

const WHATSAPP_NUMBER = "5493513484082"

const EMPTY_FORM = {
    nombre: "",
    origen: "",
    ingreso: "",
    salida: "",
    adultos: "1",
    ninos: "0",
    telefono: "",
    consulta: "",
}

function formatDate(iso) {
    if (!iso) return ""
    const [y, m, d] = iso.split("-")
    return `${d}/${m}/${y}`
}

function buildWhatsAppMessage(f) {
    const personas =
        `${f.adultos} adulto${f.adultos !== "1" ? "s" : ""}` +
        (f.ninos !== "0" ? ` + ${f.ninos} nino${f.ninos !== "1" ? "s" : ""}` : "")
    const lines = [
        "*SOLICITUD DE RESERVA - Cabanas Touring Club*",
        "",
        `*Nombre:* ${f.nombre}`,
        `*Procedencia:* ${f.origen}`,
        `*Ingreso:* ${formatDate(f.ingreso)}`,
        `*Salida:* ${formatDate(f.salida)}`,
        `*Personas:* ${personas}`,
        `*Telefono:* ${f.telefono}`,
        ...(f.consulta ? ["", `*Consulta:* ${f.consulta}`] : []),
        "",
        "_Enviado desde el sitio web_",
    ]
    return encodeURIComponent(lines.join("\n"))
}

export default function ReservaModal({ onClose }) {
    const [form, setForm] = useState(EMPTY_FORM)
    const [errors, setErrors] = useState({})

    const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

    const validate = () => {
        const required = ["nombre", "origen", "ingreso", "salida", "telefono"]
        const newErrors = {}
        required.forEach((k) => { if (!form[k].trim()) newErrors[k] = true })
        if (form.ingreso && form.salida && form.salida <= form.ingreso)
            newErrors.salida = true
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!validate()) return
        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage(form)}`
        window.open(url, "_blank", "noopener,noreferrer")
        onClose()
    }

    const inputClass = (field) =>
        `w-full bg-white/5 border ${errors[field] ? "border-red-400" : "border-white/15"} ` +
        `rounded-lg px-3 py-2 text-sm text-[#e8d5b0] placeholder-white/30 ` +
        `focus:outline-none focus:border-[#F27507] transition-colors`

    const labelClass = "block text-xs text-[#c9aa80] mb-1 tracking-wide"

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            <div
                className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
                style={{ background: "#1c1208", border: "1px solid #3d2510" }}
            >
                {/* Header */}
                <div
                    className="sticky top-0 flex items-center justify-between px-6 py-4 z-10"
                    style={{ background: "#1c1208", borderBottom: "1px solid #3d2510" }}
                >
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-[#F27507] mb-0.5">
                            Cabañas Touring Club
                        </p>
                        <h3 className="titulo-playfair italic text-[#e8d5b0] text-xl font-bold">
                            Datos de reserva
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center w-8 h-8 rounded-full transition-colors hover:bg-white/10"
                        style={{ color: "#9a7a50" }}
                        aria-label="Cerrar"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Info horarios */}
                <div className="mx-6 mt-5 rounded-xl px-4 py-3 flex flex-col gap-1.5" style={{ background: "#F27507/10", border: "1px solid #3d2510", backgroundColor: "rgba(242,117,7,0.08)" }}>
                    <div className="flex items-center gap-2 text-xs text-[#c9aa80]">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#F27507" strokeWidth="2" className="shrink-0">
                            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                        </svg>
                        <span><span className="text-[#e8d5b0]">Atención:</span> 8:00 a 21:00 hs</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#c9aa80]">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#F27507" strokeWidth="2" className="shrink-0">
                            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                        <span><span className="text-[#e8d5b0]">Check-in:</span> desde las 14:00 hs &nbsp;·&nbsp; <span className="text-[#e8d5b0]">Check-out:</span> hasta las 10:00 hs</span>
                    </div>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4" noValidate>

                    <div>
                        <label className={labelClass}>Nombre completo *</label>
                        <input
                            type="text"
                            placeholder="Ej: Juan García"
                            value={form.nombre}
                            onChange={set("nombre")}
                            className={inputClass("nombre")}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>¿De dónde viene? *</label>
                        <input
                            type="text"
                            placeholder="Ciudad / Provincia"
                            value={form.origen}
                            onChange={set("origen")}
                            className={inputClass("origen")}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>Día de ingreso *</label>
                            <input
                                type="date"
                                value={form.ingreso}
                                onChange={set("ingreso")}
                                min={new Date().toISOString().split("T")[0]}
                                className={inputClass("ingreso")}
                                style={{ colorScheme: "dark" }}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Día de salida *</label>
                            <input
                                type="date"
                                value={form.salida}
                                onChange={set("salida")}
                                min={form.ingreso || new Date().toISOString().split("T")[0]}
                                className={inputClass("salida")}
                                style={{ colorScheme: "dark" }}
                            />
                            {errors.salida && (
                                <p className="text-red-400 text-xs mt-1">La salida debe ser posterior al ingreso.</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>Adultos *</label>
                            <select
                                value={form.adultos}
                                onChange={set("adultos")}
                                className={inputClass("adultos")}
                            >
                                {[1,2,3,4,5,6,7,8].map((n) => (
                                    <option key={n} value={String(n)} style={{ background: "#1c1208" }}>{n}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Niños</label>
                            <select
                                value={form.ninos}
                                onChange={set("ninos")}
                                className={inputClass("ninos")}
                            >
                                {[0,1,2,3,4,5,6].map((n) => (
                                    <option key={n} value={String(n)} style={{ background: "#1c1208" }}>{n}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className={labelClass}>Teléfono de contacto *</label>
                        <input
                            type="tel"
                            placeholder="Ej: +54 9 351 123-4567"
                            value={form.telefono}
                            onChange={set("telefono")}
                            className={inputClass("telefono")}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>Consulta adicional <span className="text-white/30">(opcional)</span></label>
                        <textarea
                            placeholder="¿Tenés alguna pregunta o necesidad especial?"
                            value={form.consulta}
                            onChange={set("consulta")}
                            rows={3}
                            className={`${inputClass("consulta")} resize-none`}
                        />
                    </div>

                    <p className="text-xs text-white/25 -mt-1">
                        Al enviar se abrirá WhatsApp con tu consulta lista para mandar.
                    </p>

                    <button
                        type="submit"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-medium text-white transition-colors"
                        style={{ background: "#25D366" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#1fba57")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#25D366")}
                    >
                        <svg className="w-4 h-4 fill-white shrink-0" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        Enviar por WhatsApp
                    </button>
                </form>
            </div>
        </div>
    )
}
